(() => {
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const ZOOM_STEP = 0.5;

  let viewer;
  let stage;
  let viewerImage;
  let counter;

  let images = [];
  let currentIndex = 0;

  let imageLoadSequence = 0;

  let scale = 1;
  let translateX = 0;
  let translateY = 0;

  let pointerStartX = 0;
  let pointerStartY = 0;
  let startTranslateX = 0;
  let startTranslateY = 0;

  let touchStartDistance = 0;
  let touchStartScale = 1;

  let swipeStartX = 0;
  let swipeStartY = 0;

  let dragging = false;

  function createViewer() {
    if (document.querySelector('.krigerheim-viewer')) return;

    viewer = document.createElement('div');
    viewer.className = 'krigerheim-viewer';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Product image viewer');

    viewer.innerHTML = `
      <div class="krigerheim-viewer__stage">
        <img
          class="krigerheim-viewer__image"
          alt=""
          draggable="false"
        >
      </div>

      <div class="krigerheim-viewer__counter"></div>

      <button
        type="button"
        class="krigerheim-viewer__close"
        aria-label="Close image viewer"
      >×</button>

      <button
        type="button"
        class="krigerheim-viewer__previous"
        aria-label="Previous image"
      >‹</button>

      <button
        type="button"
        class="krigerheim-viewer__next"
        aria-label="Next image"
      >›</button>

      <div class="krigerheim-viewer__controls">
        <button
          type="button"
          class="krigerheim-viewer__zoom-out"
          aria-label="Zoom out"
        >−</button>

        <button
          type="button"
          class="krigerheim-viewer__reset"
          aria-label="Reset zoom"
        >100%</button>

        <button
          type="button"
          class="krigerheim-viewer__zoom-in"
          aria-label="Zoom in"
        >+</button>
      </div>
    `;

    document.body.appendChild(viewer);

    stage = viewer.querySelector('.krigerheim-viewer__stage');
    viewerImage = viewer.querySelector('.krigerheim-viewer__image');
    counter = viewer.querySelector('.krigerheim-viewer__counter');

    viewer
      .querySelector('.krigerheim-viewer__close')
      .addEventListener('click', closeViewer);

    viewer
      .querySelector('.krigerheim-viewer__previous')
      .addEventListener('click', () => changeImage(-1));

    viewer
      .querySelector('.krigerheim-viewer__next')
      .addEventListener('click', () => changeImage(1));

    viewer
      .querySelector('.krigerheim-viewer__zoom-in')
      .addEventListener('click', () => {
        setScale(scale + ZOOM_STEP);
      });

    viewer
      .querySelector('.krigerheim-viewer__zoom-out')
      .addEventListener('click', () => {
        setScale(scale - ZOOM_STEP);
      });

    viewer
      .querySelector('.krigerheim-viewer__reset')
      .addEventListener('click', resetTransform);

    stage.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    stage.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    stage.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });

    stage.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    stage.addEventListener('touchend', handleTouchEnd, {
      passive: false,
    });

    stage.addEventListener('dblclick', () => {
      if (scale > 1) {
        resetTransform();
      } else {
        setScale(2.5);
      }
    });

    document.addEventListener('keydown', handleKeyboard);
  }

  function collectImages(gallery) {
    const results = [];
    const seen = new Set();

    gallery
      .querySelectorAll('[data-media-id] img')
      .forEach((img) => {
        let src =
          img.currentSrc ||
          img.src ||
          img.getAttribute('src');

        if (!src) return;

        // Prefer the highest-resolution candidate when available.
        if (img.srcset) {
          const candidates = img.srcset
            .split(',')
            .map((candidate) => candidate.trim())
            .map((candidate) => {
              const parts = candidate.split(/\s+/);
              return {
                src: parts[0],
                width: parseInt(parts[1], 10) || 0,
              };
            })
            .sort((a, b) => b.width - a.width);

          if (candidates[0]?.src) {
            src = candidates[0].src;
          }
        }

        const cleanSrc = src.split('?')[0];

        if (seen.has(cleanSrc)) return;
        seen.add(cleanSrc);

        const mediaElement = img.closest('[data-media-id]');

        results.push({
          src,
          alt: img.alt || '',
          mediaId:
            mediaElement?.dataset.mediaId ||
            mediaElement?.getAttribute('data-media-id') ||
            '',
        });
      });

    return results;
  }

  function getCurrentGalleryMediaId(gallery) {
    const currentThumbnail = gallery.querySelector(
      '.thumbnail[aria-current="true"]'
    );

    const target =
      currentThumbnail
        ?.closest('[data-target]')
        ?.getAttribute('data-target') ||
      currentThumbnail?.dataset.target ||
      '';

    if (!target) return '';

    return target.split('-').pop();
  }

  function openViewer(gallery) {
    images = collectImages(gallery);

    if (!images.length) return;

    const activeMediaId = getCurrentGalleryMediaId(gallery);

    currentIndex = images.findIndex(
      (item) =>
        item.mediaId &&
        activeMediaId &&
        item.mediaId.includes(activeMediaId)
    );

    if (currentIndex < 0) {
      currentIndex = 0;
    }

    loadCurrentImage();

    viewer.classList.add('is-open');
    document.body.classList.add('krigerheim-viewer-open');

    viewer
      .querySelector('.krigerheim-viewer__close')
      .focus();
  }

  function closeViewer() {
      imageLoadSequence++;
      
      viewer.classList.remove('is-open');
      document.body.classList.remove('krigerheim-viewer-open');

      resetTransform();
    }

  function makePreviewSrc(src, width = 96) {
    try {
      const url = new URL(src, window.location.origin);

      /*
      * Shopify CDN images support resized versions through
      * the width query parameter.
      */
      if (
        url.hostname.includes('cdn.shopify.com') ||
        url.pathname.includes('/cdn/shop/')
      ) {
        url.searchParams.set('width', String(width));
        return url.toString();
      }

      return src;
    } catch (error) {
      return src;
    }
  }

  function delay(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  async function preloadAndDecode(src) {
    const image = new Image();

    image.decoding = 'async';
    image.src = src;

    try {
      await image.decode();
    } catch (error) {
      await new Promise((resolve, reject) => {
        if (image.complete && image.naturalWidth > 0) {
          resolve();
          return;
        }

        image.onload = resolve;
        image.onerror = reject;
      });
    }

    return image;
  }

  function preloadNeighbourImages() {
    if (!images.length) return;

    /*
    * Respect users who explicitly enabled reduced-data mode.
    */
    if (
      navigator.connection &&
      navigator.connection.saveData
    ) {
      return;
    }

    const previousIndex =
      (currentIndex - 1 + images.length) % images.length;

    const nextIndex =
      (currentIndex + 1) % images.length;

    [previousIndex, nextIndex].forEach((index) => {
      const item = images[index];

      if (!item?.src) return;

      const preload = new Image();
      preload.src = item.src;
    });
  }

  async function loadCurrentImage() {
    const item = images[currentIndex];
    if (!item) return;

    /*
    * Each navigation action gets its own sequence number.
    * If somebody quickly presses next-next-next, an older
    * image finishing later cannot overwrite the newest one.
    */
    const sequence = ++imageLoadSequence;

    resetTransform();

    counter.textContent =
      `${currentIndex + 1} / ${images.length}`;

    viewerImage.alt = item.alt || '';

    /*
    * Immediately show a tiny version of the destination image.
    * Enlarging 96px produces the intentionally soft preview.
    */
    const previewSrc = makePreviewSrc(item.src, 96);

    viewerImage.classList.remove('is-sharpening');
    viewerImage.classList.add('is-preview');

    viewerImage.src = previewSrc;

    try {
      /*
      * Begin loading the real image while the customer sees
      * the low-resolution preview.
      *
      * The small delay ensures that even cached images get
      * a subtle visible transition rather than an abrupt flash.
      */
      const [fullImage] = await Promise.all([
        preloadAndDecode(item.src),
        delay(180),
      ]);

      /*
      * The customer may already have navigated elsewhere.
      */
      if (sequence !== imageLoadSequence) return;

      /*
      * Because fullImage has already been downloaded and decoded,
      * assigning it now should be visually immediate.
      */
      viewerImage.src = fullImage.src;

      try {
        await viewerImage.decode();
      } catch (error) {
        // Browser already has the image decoded or will paint it normally.
      }

      if (sequence !== imageLoadSequence) return;

      /*
      * Start fully blurred, then transition to sharp.
      */
      viewerImage.classList.remove('is-sharpening');
      viewerImage.classList.add('is-preview');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (sequence !== imageLoadSequence) return;

          viewerImage.classList.remove('is-preview');
          viewerImage.classList.add('is-sharpening');
        });
      });

      preloadNeighbourImages();
    } catch (error) {
      /*
      * If high-resolution loading fails, leave the preview visible
      * instead of presenting a blank viewer.
      */
      console.warn(
        'Krigerheim image viewer: full image failed to load.',
        error
      );
    }
  }

  function changeImage(direction) {
    if (!images.length) return;

    currentIndex += direction;

    if (currentIndex < 0) {
      currentIndex = images.length - 1;
    }

    if (currentIndex >= images.length) {
      currentIndex = 0;
    }

    loadCurrentImage();
  }

  function updateTransform() {
    viewerImage.style.transform =
      `translate(-50%, -50%) ` +
      `translate(${translateX}px, ${translateY}px) ` +
      `scale(${scale})`;

    const percent = Math.round(scale * 100);

    const resetButton = viewer.querySelector(
      '.krigerheim-viewer__reset'
    );

    if (resetButton) {
      resetButton.textContent = `${percent}%`;
    }
  }

  function setScale(newScale) {
    scale = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, newScale)
    );

    if (scale === 1) {
      translateX = 0;
      translateY = 0;
    }

    updateTransform();
  }

  function resetTransform() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    updateTransform();
  }

  function handleWheel(event) {
    if (!viewer.classList.contains('is-open')) return;

    event.preventDefault();

    const delta =
      event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;

    setScale(scale + delta);
  }

  function handlePointerDown(event) {
    if (event.pointerType === 'touch') return;
    if (scale <= 1) return;

    dragging = true;

    pointerStartX = event.clientX;
    pointerStartY = event.clientY;

    startTranslateX = translateX;
    startTranslateY = translateY;

    stage.classList.add('is-dragging');
  }

  function handlePointerMove(event) {
    if (!dragging) return;

    translateX =
      startTranslateX +
      (event.clientX - pointerStartX);

    translateY =
      startTranslateY +
      (event.clientY - pointerStartY);

    updateTransform();
  }

  function handlePointerUp() {
    dragging = false;
    stage?.classList.remove('is-dragging');
  }

  function getTouchDistance(touches) {
    const x =
      touches[0].clientX -
      touches[1].clientX;

    const y =
      touches[0].clientY -
      touches[1].clientY;

    return Math.hypot(x, y);
  }

  function handleTouchStart(event) {
    if (event.touches.length === 2) {
      event.preventDefault();

      touchStartDistance =
        getTouchDistance(event.touches);

      touchStartScale = scale;
      return;
    }

    if (event.touches.length === 1) {
      const touch = event.touches[0];

      swipeStartX = touch.clientX;
      swipeStartY = touch.clientY;

      pointerStartX = touch.clientX;
      pointerStartY = touch.clientY;

      startTranslateX = translateX;
      startTranslateY = translateY;
    }
  }

  function handleTouchMove(event) {
    if (event.touches.length === 2) {
      event.preventDefault();

      const distance =
        getTouchDistance(event.touches);

      if (!touchStartDistance) return;

      const ratio =
        distance / touchStartDistance;

      scale = Math.min(
        MAX_SCALE,
        Math.max(
          MIN_SCALE,
          touchStartScale * ratio
        )
      );

      updateTransform();
      return;
    }

    if (
      event.touches.length === 1 &&
      scale > 1
    ) {
      event.preventDefault();

      const touch = event.touches[0];

      translateX =
        startTranslateX +
        (touch.clientX - pointerStartX);

      translateY =
        startTranslateY +
        (touch.clientY - pointerStartY);

      updateTransform();
    }
  }

  function handleTouchEnd(event) {
    if (scale > 1) {
      return;
    }

    if (event.changedTouches.length !== 1) {
      return;
    }

    const touch = event.changedTouches[0];

    const deltaX =
      touch.clientX - swipeStartX;

    const deltaY =
      touch.clientY - swipeStartY;

    if (
      Math.abs(deltaX) > 60 &&
      Math.abs(deltaX) > Math.abs(deltaY)
    ) {
      if (deltaX < 0) {
        changeImage(1);
      } else {
        changeImage(-1);
      }
    }
  }

  function handleKeyboard(event) {
    if (!viewer?.classList.contains('is-open')) return;

    switch (event.key) {
      case 'Escape':
        closeViewer();
        break;

      case 'ArrowLeft':
        changeImage(-1);
        break;

      case 'ArrowRight':
        changeImage(1);
        break;

      case '+':
      case '=':
        setScale(scale + ZOOM_STEP);
        break;

      case '-':
        setScale(scale - ZOOM_STEP);
        break;

      case '0':
        resetTransform();
        break;
    }
  }

  function initialiseProductGallery(gallery) {
    if (gallery.dataset.krigerheimViewer === 'true') return;

    const mediaItems = gallery.querySelectorAll(
      '[data-media-id]'
    );

    mediaItems.forEach((media) => {
      const image = media.querySelector('img');

      if (!image) return;

      image.style.cursor = 'zoom-in';

      media.addEventListener(
        'click',
        (event) => {
          const clickedImage =
            event.target.closest('img');

          if (!clickedImage) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          openViewer(gallery);
        },
        true
      );
    });

    gallery.dataset.krigerheimViewer = 'true';
  }

  function initialise() {
    createViewer();

    document
      .querySelectorAll('media-gallery')
      .forEach(initialiseProductGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialise
    );
  } else {
    initialise();
  }

  document.addEventListener(
    'shopify:section:load',
    initialise
  );
})();